import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Refresh from '@mui/icons-material/Refresh';

function itemIcon(status) {
  if (status === 'ok') return <CheckCircle color="success" fontSize="small" />;
  if (status === 'error' || status === 'missing') {
    return <ErrorOutline color="error" fontSize="small" />;
  }
  return <WarningAmber color="warning" fontSize="small" />;
}

function statusColor(status) {
  if (status === 'ok') return 'success';
  if (status === 'error' || status === 'missing') return 'error';
  return 'warning';
}

export default function ClientReadinessPanel({
  readiness,
  loading,
  error,
  onRefresh,
}) {
  return (
    <Card sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: '#f0f6fc' }}>
              What To Do Next
            </Typography>
            <Typography variant="body2" sx={{ color: '#7d8590' }}>
              Client readiness for publishing, search data, cadence, and auth.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {readiness && (
              <Chip
                label={readiness.overall_ready ? 'Ready' : 'Needs attention'}
                color={readiness.overall_ready ? 'success' : 'warning'}
                size="small"
              />
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={onRefresh}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {loading && !readiness ? (
          <Box sx={{ py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : readiness ? (
          <>
            <List dense sx={{ py: 0 }}>
              {(readiness.items || []).map((item) => (
                <ListItem
                  key={item.key}
                  sx={{
                    px: 0,
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Box sx={{ pt: 0.5 }}>{itemIcon(item.status)}</Box>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography sx={{ color: '#f0f6fc', fontWeight: 600 }}>
                          {item.label}
                        </Typography>
                        <Chip
                          label={item.status}
                          color={statusColor(item.status)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: '#7d8590' }}>
                        {item.detail}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {readiness.cadence && (
              <Typography variant="caption" sx={{ color: '#7d8590', display: 'block', mt: 1 }}>
                This week: {readiness.cadence.count}/{readiness.cadence.target}{' '}
                posts in {readiness.cadence.timezone}
              </Typography>
            )}

            {(readiness.auth_errors || []).length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {(readiness.auth_errors || []).slice(0, 3).map((err) => (
                  <Typography key={`${err.source}-${err.post_id || err.account_id || err.message}`} variant="body2">
                    {err.platform ? `${err.platform}: ` : ''}
                    {err.message}
                  </Typography>
                ))}
              </Alert>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: '#7d8590' }}>
            No readiness data yet.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
